import type { AssistConsentState } from './assistConsentMachine';

/** How one audio toggle should currently render. */
export interface AudioTile {
  /** Can the wearer act on this right now? False renders the tile disabled. */
  readonly available: boolean;
  /** Is the audio it controls actually flowing? */
  readonly on: boolean;
  /** Tile glyph (BMP only - the Unity strip renders in Arial; kept identical here). */
  readonly glyph: string;
  /** Micro label under the glyph. Names the STATE, not the action. */
  readonly label: string;
}

export const MIC_ON_GLYPH = '●';
export const MIC_OFF_GLYPH = '○';
export const ASSISTANT_ON_GLYPH = '◈';
export const ASSISTANT_OFF_GLYPH = '◇';

/**
 * The wearer's audio rules, in one pure place (Plan-01 §2.2 / D5 / D6). Port
 * of `WearerAudioPolicy.cs`.
 *
 * Two choices belong to the wearer and neither is the consent machine's:
 * muting THEIR OWN microphone, and muting what the assistant sends back.
 * Consent decides whether a microphone may exist at all; the self-mute
 * decides whether the one consent allows is open. They compose one way only -
 * a mute can never grant what consent withheld:
 *
 *   mic publishes  <=>  consent == live  AND  NOT selfMuted
 *
 * The tile helpers exist so the hand menu shows what is TRUE rather than what
 * was last pressed: a wearer whose microphone is off because assistance is
 * paused must not read "MIC ON" just because they never muted themselves.
 */

/**
 * The consent half, alone: RA-C2/RA-C6 allow a microphone in `live` and
 * nowhere else. Stated once here and used by the consent machine too, so the
 * rule cannot come to mean two things.
 */
export function consentAllowsMic(consent: AssistConsentState): boolean {
  return consent === 'live';
}

/**
 * Should the microphone be published and unmuted? Consent first, the wearer's
 * own mute second - self-mute only ever subtracts.
 */
export function micShouldPublish(consent: AssistConsentState, selfMuted: boolean): boolean {
  return consentAllowsMic(consent) && !selfMuted;
}

/**
 * The wearer's own microphone tile. Unavailable off-session (nothing to talk
 * into) and while PAUSED - the master pause already silenced the mic, and a
 * self-mute toggle that appeared to un-pause assistance would be lying about
 * a consent decision.
 */
export function micTile(consent: AssistConsentState, selfMuted: boolean): AudioTile {
  if (!consentAllowsMic(consent)) {
    // Paused reads as an explicit OFF (it is, and the wearer chose it);
    // off/awaiting has no session to describe at all.
    const paused = consent === 'paused';
    return { available: false, on: false, glyph: MIC_OFF_GLYPH, label: paused ? 'MIC OFF' : 'MIC' };
  }

  return {
    available: true,
    on: !selfMuted,
    glyph: selfMuted ? MIC_OFF_GLYPH : MIC_ON_GLYPH,
    label: selfMuted ? 'MIC OFF' : 'MIC ON',
  };
}

/**
 * The "can I hear the assistant" tile. Unavailable with nobody in the session
 * to hear, and while paused (every assistant track is unsubscribed then - D5).
 */
export function assistantAudioTile(
  consent: AssistConsentState,
  assistantsMuted: boolean,
  assistantCount: number,
): AudioTile {
  if (assistantCount <= 0 || consent === 'off' || consent === 'awaitingAllow') {
    return { available: false, on: false, glyph: ASSISTANT_OFF_GLYPH, label: 'HEAR' };
  }

  if (consent === 'paused') {
    return { available: false, on: false, glyph: ASSISTANT_OFF_GLYPH, label: 'HEAR OFF' };
  }

  return {
    available: true,
    on: !assistantsMuted,
    glyph: assistantsMuted ? ASSISTANT_OFF_GLYPH : ASSISTANT_ON_GLYPH,
    label: assistantsMuted ? 'HEAR OFF' : 'HEAR ON',
  };
}
